import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactModal from 'react-modal'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import {
  getConfiguredBenefits,
  getConfiguredText,
  getHighlights,
  memberCopy,
} from './compact-copy'
import styles from './compact-diamond-membership.scss'

const focusBagSummary = () => {
  document
    .querySelector('.shopping-bag .cart-body__purchase')
    ?.focus({ preventScroll: true })
}

const CompactDiamondMembership = ({
  t,
  language,
  shop,
  loyaltyData,
  busy,
  onAdd,
}) => {
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [status, setStatus] = useState('')
  const pending = useRef(false)
  const mounted = useRef(true)
  const successful = useRef(false)
  const closeButton = useRef(null)
  const headerRef = useRef(null)
  const footerRef = useRef(null)
  const [dialogReady, setDialogReady] = useState(false)
  const [fixedRegions, setFixedRegions] = useState(false)
  const [appElements, setAppElements] = useState([])
  useEffect(
    () => () => {
      mounted.current = false
      // A confirmed cart update can remove this offer before the add promise
      // settles. Restore focus after ReactModal's own unmount cleanup runs.
      if (pending.current || successful.current)
        Promise.resolve().then(focusBagSummary)
    },
    [],
  )

  useEffect(() => {
    if (!open || !dialogReady || typeof ResizeObserver === 'undefined')
      return undefined
    const measure = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight
      const available =
        window.innerWidth >= 768 ? viewportHeight - 48 : viewportHeight * 0.92
      const fixedHeight =
        (headerRef.current?.scrollHeight || 0) +
        (footerRef.current?.scrollHeight || 0)
      setFixedRegions(fixedHeight + 120 <= available)
    }
    const observer = new ResizeObserver(measure)
    if (headerRef.current) observer.observe(headerRef.current)
    if (footerRef.current) observer.observe(footerRef.current)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    measure()
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [open, dialogReady])

  const copy = (name) => memberCopy(t, language, name)
  const benefits = getConfiguredBenefits(t, loyaltyData)
  const highlights = getHighlights(t, language, shop, benefits)
  const moreBenefits = benefits.filter(
    (benefit) =>
      (shop === 'th' && benefit.title_key === 'DIAMOND_EXCLUSIVE_CASHBACK') ||
      !highlights.some(
        (highlight) => highlight.title_key === benefit.title_key,
      ),
  )
  const price = getConfiguredText(
    t,
    loyaltyData.diamond_product_subscription_price_key,
  )
  const terms = getConfiguredText(t, 'DIAMOND_SUBSCRIPTION_HOW_IT_WORK')
  const unavailable =
    !price ||
    !terms ||
    !highlights.length ||
    !loyaltyData.diamond_product?.id_product ||
    !loyaltyData.diamond_product?.id_product_attribute
  const disabled = busy || adding || unavailable || status === 'added'
  const add = async () => {
    if (disabled || pending.current) return
    pending.current = true
    setAdding(true)
    setStatus('')
    if (open) closeButton.current?.focus()
    try {
      const confirmed = await onAdd()
      if (mounted.current) {
        setStatus(confirmed ? 'added' : 'error')
        if (confirmed) {
          successful.current = true
          setOpen(false)
        }
      }
    } catch (error) {
      if (mounted.current) setStatus('error')
    } finally {
      pending.current = false
      if (mounted.current) setAdding(false)
    }
  }
  const openBenefits = () => {
    // The bag lives in #modal, outside Next's app root. Hide both from AT.
    setAppElements(
      Array.from(document.body.children).filter(
        (element) =>
          !element.classList.contains('diamond-benefits-portal') &&
          element.getAttribute('aria-hidden') !== 'true' &&
          !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName),
      ),
    )
    setOpen(true)
  }
  const addLabel = adding || busy ? copy('adding') : copy('add')
  const highlightList = (detailed) => (
    <ul
      className={`diamond-benefits-list${
        detailed ? ' diamond-benefits-list--detailed' : ''
      }`}
    >
      {highlights.map((benefit) => (
        <li key={benefit.title_key}>
          <span className="diamond-benefit-icon" aria-hidden="true">
            {detailed && benefit.icon_url_blue ? (
              <Icon src={benefit.icon_url_blue} size={ICON_SIZE.medium} />
            ) : (
              '✓'
            )}
          </span>
          <span>
            {benefit.text || (
              <React.Fragment>
                {benefit.before}
                <strong>{benefit.lead}</strong>
                {benefit.tail}
              </React.Fragment>
            )}
          </span>
        </li>
      ))}
    </ul>
  )

  return (
    <section className="diamond-membership" aria-label={copy('title')}>
      <style jsx>{styles}</style>
      <div className="diamond-membership-heading">
        <span className="diamond-membership-gem" aria-hidden="true">
          <Icon src={ICONS.diamond} size={ICON_SIZE.medium} />
        </span>
        <h3>{copy('title')}</h3>
      </div>
      {highlightList(false)}
      <button
        type="button"
        className="diamond-membership-link"
        onClick={openBenefits}
        aria-haspopup="dialog"
      >
        {copy('view')}
        <span aria-hidden="true"> →</span>
      </button>
      {price && <p className="diamond-membership-price">{price}</p>}
      {unavailable && (
        <p className="diamond-membership-message">{copy('unavailable')}</p>
      )}
      {!open && status && (
        <p
          className="diamond-membership-message"
          role={status === 'error' ? 'alert' : 'status'}
        >
          {copy(status)}
        </p>
      )}
      <button
        type="button"
        className="diamond-membership-add"
        disabled={disabled}
        onClick={add}
      >
        {addLabel}
        <span aria-hidden="true"> +</span>
      </button>
      <ReactModal
        isOpen={open}
        portalClassName="diamond-benefits-portal"
        appElement={appElements.length ? appElements : undefined}
        ariaHideApp={!!appElements.length}
        aria={{ labelledby: 'diamond-benefits-title' }}
        contentElement={(dialogProps, children) => (
          <div {...dialogProps} lang={language}>
            {children}
          </div>
        )}
        className={`diamond-benefits-dialog${
          fixedRegions ? ' diamond-benefits-dialog--fixed' : ''
        }`}
        overlayClassName="diamond-benefits-overlay"
        bodyOpenClassName="diamond-benefits-open"
        onRequestClose={() => setOpen(false)}
        onAfterOpen={() => {
          closeButton.current?.focus()
          setDialogReady(true)
        }}
        onAfterClose={() => {
          if (mounted.current) {
            setDialogReady(false)
            setFixedRegions(false)
          }
          if (successful.current) {
            successful.current = false
            focusBagSummary()
          }
        }}
        shouldCloseOnOverlayClick
        preventScroll
      >
        <header className="diamond-benefits-header" ref={headerRef}>
          <div className="diamond-membership-heading">
            <span className="diamond-membership-gem" aria-hidden="true">
              <Icon src={ICONS.diamond} size={ICON_SIZE.medium} />
            </span>
            <div>
              <h2 id="diamond-benefits-title">{copy('title')}</h2>
              <p>{copy('intro')}</p>
            </div>
          </div>
          <button
            type="button"
            className="diamond-benefits-close"
            ref={closeButton}
            onClick={() => setOpen(false)}
            aria-label={copy('close')}
          >
            <Icon src={ICONS.close} size={ICON_SIZE.medium} />
          </button>
        </header>
        <div className="diamond-benefits-content">
          {highlightList(true)}
          {!!moreBenefits.length && (
            <section className="diamond-benefits-more">
              <h3>{copy('more')}</h3>
              <ul>
                {moreBenefits.map((benefit) => (
                  <li key={benefit.title_key}>{benefit.text}</li>
                ))}
              </ul>
            </section>
          )}
          {shop === 'th' && (
            <details className="diamond-benefits-details">
              <summary>{copy('eligibility')}</summary>
              <p>{copy('eligibilityBody')}</p>
            </details>
          )}
          {terms && (
            <details className="diamond-benefits-details">
              <summary>{copy('terms')}</summary>
              <div
                className="diamond-benefits-terms"
                // Same localized membership terms used on the existing Diamond page.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: terms }}
              />
            </details>
          )}
        </div>
        <footer className="diamond-benefits-footer" ref={footerRef}>
          {price && <p className="diamond-membership-price">{price}</p>}
          {shop === 'th' && terms && (
            <p className="diamond-membership-renewal">{copy('renewal')}</p>
          )}
          {unavailable && (
            <p className="diamond-membership-message">{copy('unavailable')}</p>
          )}
          {status === 'error' && (
            <p className="diamond-membership-message" role="alert">
              {copy('error')}
            </p>
          )}
          <button
            type="button"
            className="diamond-membership-add diamond-membership-add--primary"
            disabled={disabled}
            onClick={add}
          >
            {addLabel}
            <span aria-hidden="true"> +</span>
          </button>
          <button
            type="button"
            className="diamond-benefits-back"
            onClick={() => setOpen(false)}
          >
            {copy('back')}
          </button>
        </footer>
      </ReactModal>
    </section>
  )
}

CompactDiamondMembership.propTypes = {
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  shop: PropTypes.string.isRequired,
  loyaltyData: PropTypes.shape({
    summary_benefits: PropTypes.arrayOf(
      PropTypes.shape({ title_key: PropTypes.string }),
    ),
    diamond_product_subscription_price_key: PropTypes.string,
    diamond_product: PropTypes.shape({
      id_product: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id_product_attribute: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
  }).isRequired,
  busy: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
}

export default CompactDiamondMembership
